import { Children, type FC, type ReactNode, useLayoutEffect, useRef } from 'react';
import { Button, Stepper } from '@george-labs.com/design-system';
import classNames from 'classnames';

import { ENTER_KEY, SPACE_KEY } from '@luna/a11y';
import { t } from '@luna/i18n';

import styles from './CoverFlowCarousel.module.scss';

interface Props {
    activeIndex: number;
    onActiveIndexChange: (index: number) => void;
    showStepper?: boolean;
    className?: string;
    children: ReactNode;
}

const navigateOnKeyDown = (
    event: { key: string; preventDefault: () => void },
    navigate: () => void,
) => {
    if (event.key === ENTER_KEY || event.key === SPACE_KEY) {
        event.preventDefault();
        navigate();
    }
};

export const CoverFlowCarousel: FC<Props> = ({
    activeIndex,
    onActiveIndexChange,
    showStepper = false,
    className,
    children,
}) => {
    const trackRef = useRef<HTMLUListElement>(null);
    const totalItems = Children.count(children);
    const isFirst = activeIndex === 0;
    const isLast = activeIndex === totalItems - 1;

    useLayoutEffect(() => {
        trackRef.current?.style.setProperty('--carousel-active-index', `${activeIndex}`);
    }, [activeIndex]);

    const navigateToPrevious = () => {
        if (!isFirst) {
            onActiveIndexChange(activeIndex - 1);
        }
    };

    const navigateToNext = () => {
        if (!isLast) {
            onActiveIndexChange(activeIndex + 1);
        }
    };

    return (
        <section className={className} aria-roledescription="carousel">
            <div className={styles.container}>
                <div className={styles.viewport}>
                    <ul ref={trackRef} className={styles.track} aria-live="polite">
                        {Children.map(children, (child, index) => {
                            const distance = Math.abs(index - activeIndex);
                            const isSide = distance === 1;
                            const isActive = distance === 0;
                            const isHidden = distance > 1;

                            const navigateToItem = () => onActiveIndexChange(index);
                            const navigateToItemOnKeyDown = (event: {
                                key: string;
                                preventDefault: () => void;
                            }) => navigateOnKeyDown(event, navigateToItem);

                            return (
                                <li
                                    className={classNames(styles.item, {
                                        [styles.itemActive]: isActive,
                                        [styles.itemSide]: isSide,
                                        [styles.itemHidden]: isHidden,
                                    })}
                                    aria-roledescription="slide"
                                    aria-label={`${index + 1} / ${totalItems}`}
                                    aria-hidden={isHidden}
                                >
                                    <div className={styles.itemWrapper}>{child}</div>
                                    {isSide && (
                                        <button
                                            type="button"
                                            className={styles.itemOverlayButton}
                                            onClick={navigateToItem}
                                            onKeyDown={navigateToItemOnKeyDown}
                                            aria-label={`${index + 1} / ${totalItems}`}
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <Button
                    hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                    fullWidth={Button.FULL_WIDTH.NEVER}
                    size={Button.SIZE.SMALL}
                    icon={Button.ICON.CARET_LEFT}
                    hidden={isFirst}
                    className={classNames(styles.button, styles.buttonPrevious)}
                    onClick={navigateToPrevious}
                >
                    {t('app.back')}
                </Button>
                <Button
                    hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                    fullWidth={Button.FULL_WIDTH.NEVER}
                    size={Button.SIZE.SMALL}
                    icon={Button.ICON.CARET_RIGHT}
                    hidden={isLast}
                    className={classNames(styles.button, styles.buttonNext)}
                    onClick={navigateToNext}
                >
                    {t('app.next')}
                </Button>
            </div>
            {showStepper && (
                <Stepper
                    steps={[...Array(totalItems).keys()].map(String)}
                    currentStepIndex={activeIndex}
                    className={styles.stepper}
                    minimal
                />
            )}
        </section>
    );
};
